<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { mapsApi } from '$lib/global/maps/api';
    import { turnOffAutoComplete } from '$lib/utils/autoComplete';
    import { cn } from '$lib/utils/cn';

    import debounce from 'lodash/debounce';
    import uniqueId from 'lodash/uniqueId';
    import { onMount } from 'svelte';
    import { afterUpdate } from 'svelte';
    import { writable } from 'svelte/store';
    import clickOutside from 'svelte-outside-click';

    import Dropdown from '../../../_wrappers/Dropdown.svelte';
    import Emoji from '../../../_wrappers/Emoji.svelte';
    import { createInputToDropdownKeyHandler } from '../DropdownInput/dropdownKeyboardNavigation';
    import DropdownMapsButtons from '../DropdownInput/DropdownMapsButtons.svelte';
    import StatusIcon from '../StatusIcon.svelte';

    import type { MapsAutoCompleteTypes, Place, PlaceDetails } from '$lib/global/maps/maps.types';
    import type { Language } from '$lib/types/Language';
    import type { FormApi } from 'final-form';

    const EVENTS: Record<MapsAutoCompleteTypes, MapsAutoCompleteTypes[]> = {
        street: ['fullAddress'],
        postal: ['street', 'fullAddress'],
        city: ['postal', 'street', 'fullAddress'],
        fullAddress: [],
    };

    export let type: MapsAutoCompleteTypes;
    export let placeholder = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let active = false;
    export let finalFormInput: {
        name: string;
        value: string;
        onChange: (value: string) => void;
        onBlur: (event: Event) => void;
        onFocus: (event: Event) => void;
    };
    export let errorMessage = '';
    export let focusColor = '';
    export let outlineColor = '';
    export let showEmoji = false;
    export let emoji = '';
    export let language: Language = 'en';
    export let form: FormApi<any> | undefined = undefined;

    // Final Form may set the value to a nested object (e.g. { street: '...', city: '...' })
    // when a fullAddress input creates address.* fields that collide with an 'address' field name.
    // Extract the relevant string by the input type to avoid displaying [object Object].
    const extractStringValue = (val: unknown): string => {
        if (typeof val === 'object' && val !== null) {
            return (val as Record<string, string>)[type] || '';
        }
        return (val as string) || '';
    };

    let showError = invalid && touched;
    let inputContainerClasses: string;
    let inputClasses: string;
    let autoCompleteDropdownOpen = false;
    let inputFocus = true;
    let value = extractStringValue(finalFormInput?.value);
    let mapsApiKey = '';
    let valueFromEvent = writable('');
    let sessionToken = '';
    let inputElement: HTMLInputElement | undefined;
    let placeDetails: PlaceDetails | undefined;

    const dropdownId = uniqueId('dropdown');

    onMount(async () => {
        mapsApiKey = await mapsApi.getApiKey();
    });

    onMount(() => {
        sessionToken = crypto.randomUUID();
    });

    onMount(() => {
        EVENTS[type].forEach((interestedOn) => {
            document.addEventListener(`${interestedOn}:selected`, (event: any) => {
                if (!event.detail) {
                    return;
                }

                // For street type, combine street + streetNumber (e.g. "Friedenstraße 12")
                if (type === 'street') {
                    const street = event.detail.street || '';
                    const streetNumber = event.detail.streetNumber || '';

                    valueFromEvent.set(
                        [street, streetNumber]
                            .filter(Boolean)
                            .map((s: string) => s.trim())
                            .join(' '),
                    );
                } else {
                    valueFromEvent.set(event.detail[type]);
                }
            });
        });
    });

    $: {
        if ($valueFromEvent && value === '') {
            value = $valueFromEvent;
            finalFormInput.onChange(value);
        }
    }

    afterUpdate(() => {
        showError = invalid && touched;
    });

    const closeDropdown = () => {
        autoCompleteDropdownOpen = false;
    };

    const openDropdown = () => {
        autoCompleteDropdownOpen = !autoCompleteDropdownOpen;
    };

    const handleClickOutside = () => {
        if (!autoCompleteDropdownOpen || inputFocus) return;

        closeDropdown();
    };

    const handleSelectValue = async (place: Place) => {
        if (finalFormInput.name === 'fullAddress') {
            const fullAddressValue = [place.mainText, place.secondaryText].join(', ');
            value = fullAddressValue;
            finalFormInput.onChange(fullAddressValue);
        } else {
            value = place.mainText;
            finalFormInput.onChange(value);
        }

        closeDropdown();

        placeDetails = await mapsApi.places.getDetails(place.placeId, {
            apiKey: mapsApiKey,
            language,
            sessionToken,
        });

        if (placeDetails) {
            const event = new CustomEvent(`${type}:selected`, {
                detail: placeDetails,
            });
            document.dispatchEvent(event);

            // For fullAddress, populate sibling address fields with decomposed values.
            // This would ensure that for the forms where the fields exists the values are set properly.
            if (finalFormInput.name === 'fullAddress' && form) {
                const registeredFields = form.getRegisteredFields();

                const streetValue = [placeDetails.street || '', placeDetails.streetNumber || '']
                    .filter(Boolean)
                    .map((str: string) => str.trim())
                    .join(' ');

                // Check for visible address-related fields.
                const streetField = registeredFields.find((f) => ['address', 'street'].includes(f));
                const postalField = registeredFields.find((f) => ['postal', 'zip'].includes(f));
                const hasVisibleAddressField = !!streetField;

                // Fill visible fields directly when they exist.
                if (streetField) {
                    form.change(streetField, streetValue);
                }

                if (registeredFields.includes('city')) {
                    form.change('city', placeDetails.city);
                }

                if (registeredFields.includes('country')) {
                    form.change('country', placeDetails.country);
                }

                if (postalField) {
                    form.change(postalField, placeDetails.postal);
                }

                // Fall back to hidden address.* fields only when no visible address-related fields exist.
                if (!hasVisibleAddressField) {
                    form.change('address.street', streetValue);
                    form.change('address.postalCode', placeDetails.postal);
                    form.change('address.city', placeDetails.city);
                    form.change('address.country', placeDetails.country);
                }
            }
        }

        sessionToken = crypto.randomUUID();
    };

    const handleBlur = (event: FocusEvent) => {
        finalFormInput.onBlur(event);
        inputFocus = false;
    };

    const handleFocus = (event: FocusEvent) => {
        inputFocus = true;
        finalFormInput.onFocus(event);
        openDropdown();
    };

    const handleInput = (event: Event) => {
        const target = event.target as HTMLInputElement;
        value = target.value;
        finalFormInput.onChange(value);
    };

    $: handleInputKeyDown = createInputToDropdownKeyHandler(
        dropdownId,
        autoCompleteDropdownOpen,
        closeDropdown,
    );

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: inputContainerClasses = cn(
        borderRadius,
        'fun-box-border fun-w-full fun-border fun-bg-white fun-pl-4 fun-pr-6 fun-align-middle fun-ring-2 fun-ring-transparent fun-transition-all',
        {
            ['fun-pl-12']: showEmoji,
            ['fun-h-20']: invalid && showError,
            ['fun-h-14']: !invalid || !showError,
            ['fun-border-[var(--focusColor)] fun-ring-[var(--outlineColor)]']: active,
        },
    );

    $: inputClasses = cn(
        'fun-w-full fun-appearance-none fun-rounded-none fun-border-none fun-bg-transparent fun-align-middle fun-font-ui fun-text-base fun-font-light fun-text-black fun-placeholder-gray-500 fun-outline-none fun-transition-all',
        {
            'fun-h-full': !invalid || !showError,
            'fun-h-14': invalid && showError,
            'fun-pr-24': !required && pristine,
            'fun-pr-12': invalid && showError,
            'fun-pr-10': (!invalid || !showError) && (required || !pristine),
        },
    );
</script>

<div class="fun-relative fun-box-border fun-flex fun-items-center">
    {#if showEmoji}
        <div
            class="fun-absolute fun-left-4 fun-top-0 fun-flex fun-h-14 fun-select-none fun-items-center"
        >
            <u class="fun-no-underline" aria-hidden="true">
                <Emoji {emoji} size="20px" />
            </u>
        </div>
    {/if}

    {#if !required && !invalid && pristine}
        <u
            class="fun-absolute fun-right-6 fun-text-xs fun-font-light fun-text-gray-500 fun-no-underline"
            >(optional)</u
        >
    {/if}

    <StatusIcon {showError} />

    <div
        class={inputContainerClasses}
        style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
    >
        <input
            bind:this={inputElement}
            class={inputClasses}
            type="text"
            {...turnOffAutoComplete()}
            {placeholder}
            name={finalFormInput.name}
            {value}
            on:blur={handleBlur}
            on:focus={handleFocus}
            on:input={debounce(handleInput, 300)}
            on:keydown={handleInputKeyDown}
        />

        {#if errorMessage && showError}
            <div class="fun--mt-2.5 fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600">
                {errorMessage}
            </div>
        {/if}
    </div>

    {#if mapsApiKey}
        <div use:clickOutside={handleClickOutside}>
            <Dropdown
                id={dropdownId}
                open={autoCompleteDropdownOpen}
                contentClass="fun-w-full fun-box-border"
            >
                <DropdownMapsButtons
                    {type}
                    filter={value}
                    open={autoCompleteDropdownOpen}
                    handleSelect={handleSelectValue}
                    {language}
                    apiKey={mapsApiKey}
                    {sessionToken}
                    {inputElement}
                />
            </Dropdown>
        </div>
    {/if}
</div>
